import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import {
  readWebStorage,
  removeWebStorage,
  writeWebStorage,
} from './web-storage';
import { requestPostOnboardingExperience } from './post-onboarding-experience';
import { createSecureStoreKey } from './secure-store-key';

const PENDING_PREFIX = 'tabbio_post_onboarding_welcome_pending';
const SEEN_PREFIX = 'tabbio_post_onboarding_welcome_seen';
const LEGACY_PENDING_PREFIX = 'tabbio_post_onboarding_welcome_pending:';
const LEGACY_SEEN_PREFIX = 'tabbio_post_onboarding_welcome_seen:';

type WelcomeRequestListener = () => void;

const requestListeners = new Set<WelcomeRequestListener>();

function storageKey(prefix: string, userId?: string | null) {
  return userId ? createSecureStoreKey(prefix, userId) : null;
}

function legacyStorageKey(prefix: string, userId?: string | null) {
  return userId ? `${prefix}${userId}` : null;
}

function storageKeys(
  prefix: string,
  legacyPrefix: string,
  userId?: string | null,
) {
  const key = storageKey(prefix, userId);
  const legacyKey = legacyStorageKey(legacyPrefix, userId);

  if (!key) return [];
  if (Platform.OS !== 'web' || !legacyKey || legacyKey === key) return [key];
  return [key, legacyKey];
}

function readStorage(key: string) {
  if (Platform.OS === 'web') {
    return readWebStorage(key);
  }

  try {
    return SecureStore.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  if (Platform.OS === 'web') {
    writeWebStorage(key, value);
    return;
  }

  try {
    void SecureStore.setItemAsync(key, value);
  } catch {
    // Best effort only; the welcome should never block onboarding completion.
  }
}

function removeStorage(key: string) {
  if (Platform.OS === 'web') {
    removeWebStorage(key);
    return;
  }

  try {
    void SecureStore.deleteItemAsync(key);
  } catch {
    // Best effort only.
  }
}

export function markPostOnboardingWelcomePending(userId?: string | null) {
  const key = storageKey(PENDING_PREFIX, userId);
  if (!key) return;

  writeStorage(key, '1');
  requestPostOnboardingWelcome();
  requestPostOnboardingExperience();
}

export function shouldShowPostOnboardingWelcome(userId?: string | null) {
  const pendingKeys = storageKeys(
    PENDING_PREFIX,
    LEGACY_PENDING_PREFIX,
    userId,
  );
  const seenKeys = storageKeys(SEEN_PREFIX, LEGACY_SEEN_PREFIX, userId);
  if (!pendingKeys.length || !seenKeys.length) return false;

  return (
    pendingKeys.some((key) => readStorage(key) === '1') &&
    !seenKeys.some((key) => readStorage(key) === '1')
  );
}

export function hasSeenPostOnboardingWelcome(userId?: string | null) {
  const seenKeys = storageKeys(SEEN_PREFIX, LEGACY_SEEN_PREFIX, userId);
  // No user id → the welcome can never show for this session; treat as seen.
  if (!seenKeys.length) return true;

  return seenKeys.some((key) => readStorage(key) === '1');
}

export function markPostOnboardingWelcomeSeen(userId?: string | null) {
  const seenKey = storageKey(SEEN_PREFIX, userId);
  if (!seenKey) return;

  writeStorage(seenKey, '1');
  storageKeys(PENDING_PREFIX, LEGACY_PENDING_PREFIX, userId).forEach(
    removeStorage,
  );
}

export function subscribePostOnboardingWelcomeRequest(
  listener: WelcomeRequestListener,
) {
  requestListeners.add(listener);
  return () => {
    requestListeners.delete(listener);
  };
}

export function requestPostOnboardingWelcome() {
  requestListeners.forEach((listener) => listener());
}
